import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { ChevronDown, Settings, Upload } from "lucide-react";
import { fmtCamType } from "../utils/format";

interface LeaseTypeSelectorProps {
  type: string;
  tenantId: string;
  dealId: string;
  onTypeChange?: (
    tenantId: string,
    leaseType: string,
    camStructure: Record<string, unknown>
  ) => void;
  readOnly?: boolean;
}

const QUICK_OPTIONS = [
  {
    value: "NNN",
    leaseType: "NNN",
    label: "NNN (Triple Net)",
    desc: "Pro-rata share of all operating expenses",
    cam: {
      cam_type: "NNN",
      tax_reimbursement: true,
      tax_reimbursement_type: "NNN",
      insurance_reimbursement: true,
      insurance_reimbursement_type: "NNN",
    },
  },
  {
    value: "gross",
    leaseType: "gross",
    label: "Gross",
    desc: "Landlord absorbs all operating expenses",
    cam: {
      cam_type: "gross",
      tax_reimbursement: false,
      tax_reimbursement_type: "none",
      insurance_reimbursement: false,
      insurance_reimbursement_type: "none",
    },
  },
  {
    value: "base_year_stop",
    leaseType: "base_year_stop",
    label: "Base Year Stop",
    desc: "Tenant pays increases above base year amount",
    cam: {
      cam_type: "base_year_stop",
      tax_reimbursement: true,
      tax_reimbursement_type: "base_year_stop",
      insurance_reimbursement: true,
      insurance_reimbursement_type: "base_year_stop",
    },
  },
];

const BADGE_COLORS: Record<string, string> = {
  nnn: "bg-forest-50 text-forest-700 border-forest-200",
  nn: "bg-blue-50 text-blue-700 border-blue-200",
  gross: "bg-honey-50 text-honey-700 border-honey-200",
  modified_gross: "bg-honey-50 text-honey-700 border-honey-200",
  fsg: "bg-honey-50 text-honey-700 border-honey-200",
  base_year_stop: "bg-blue-50 text-blue-700 border-blue-200",
  expense_cap: "bg-blue-50 text-blue-700 border-blue-200",
  fixed_psf: "bg-forest-50 text-forest-700 border-forest-200",
};

const BADGE_LABELS: Record<string, string> = {
  nnn: "NNN",
  nn: "NN",
  gross: "Gross",
  modified_gross: "Mod. Gross",
  fsg: "FSG",
  base_year_stop: "Base Yr Stop",
  expense_cap: "Expense Cap",
  fixed_psf: "Fixed $/SF",
};

export default function LeaseTypeSelector({
  type,
  tenantId,
  dealId,
  onTypeChange,
  readOnly = false,
}: LeaseTypeSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const ref = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const updatePos = useCallback(() => {
    if (btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - r.bottom;
      const menuHeight = 320; // approximate
      const top = spaceBelow < menuHeight ? r.top - menuHeight : r.bottom + 4;
      setPos({ top: Math.max(4, top), left: r.left });
    }
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (ref.current?.contains(target) || menuRef.current?.contains(target)) return;
      setIsOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (isOpen) updatePos();
  }, [isOpen, updatePos]);

  const lower = (type || "").toLowerCase();
  const isPending = !type || lower === "pending";

  if (readOnly) {
    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold border whitespace-nowrap ${
          isPending
            ? "bg-grove-200 text-bark-500 border-grove-300"
            : BADGE_COLORS[lower] ||
              "bg-grove-100 text-bark-500 border-grove-300"
        }`}
      >
        {isPending ? "Pending" : BADGE_LABELS[lower] || fmtCamType(type)}
      </span>
    );
  }

  const handleSelect = (
    opt: (typeof QUICK_OPTIONS)[number] | null,
    action?: string
  ) => {
    setIsOpen(false);
    if (action === "upload_cam") {
      navigate(`/deals/${dealId}/documents?tab=cam_rec`);
      return;
    }
    if (action === "detail") {
      navigate(`/deals/${dealId}/reimbursements?tenant=${tenantId}`);
      return;
    }
    if (opt) {
      onTypeChange?.(tenantId, opt.leaseType, opt.cam);
    }
  };

  return (
    <div ref={ref} className="relative inline-block">
      <button
        ref={btnRef}
        onClick={() => setIsOpen(!isOpen)}
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium border cursor-pointer transition-colors whitespace-nowrap ${
          isPending
            ? "bg-honey-50 text-honey-700 border-honey-300 hover:bg-honey-100"
            : `${
                BADGE_COLORS[lower] ||
                "bg-grove-100 text-bark-500 border-grove-300"
              } hover:opacity-80`
        }`}
      >
        {isPending
          ? "Pending \u2014 select type"
          : BADGE_LABELS[lower] || fmtCamType(type)}
        <ChevronDown className="w-3 h-3 opacity-50" />
      </button>

      {isOpen && createPortal(
        <div ref={menuRef} style={{ position: "fixed", top: pos.top, left: pos.left, zIndex: 9999 }} className="w-64 bg-white border border-grove-300 rounded-lg shadow-xl py-1 animate-fade-in">
          {QUICK_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => handleSelect(opt)}
              className="w-full text-left px-3 py-2 hover:bg-grove-50 transition-colors"
            >
              <div className="text-[12px] font-semibold text-bark-700">
                {opt.label}
              </div>
              <div className="text-[10px] text-bark-400 mt-0.5">
                {opt.desc}
              </div>
            </button>
          ))}
          <div className="border-t border-grove-200 my-1" />
          <button
            onClick={() => handleSelect(null, "detail")}
            className="w-full text-left px-3 py-2 hover:bg-grove-50 transition-colors flex items-center gap-2"
          >
            <Settings className="w-3.5 h-3.5 text-bark-400 flex-shrink-0" />
            <div>
              <div className="text-[12px] font-semibold text-bark-700">
                {"Detail\u2026"}
              </div>
              <div className="text-[10px] text-bark-400">
                Define custom recovery methodology
              </div>
            </div>
          </button>
          <button
            onClick={() => handleSelect(null, "upload_cam")}
            className="w-full text-left px-3 py-2 hover:bg-grove-50 transition-colors flex items-center gap-2"
          >
            <Upload className="w-3.5 h-3.5 text-bark-400 flex-shrink-0" />
            <div>
              <div className="text-[12px] font-semibold text-bark-700">
                Upload CAM Rec
              </div>
              <div className="text-[10px] text-bark-400">
                Upload reconciliation document
              </div>
            </div>
          </button>
        </div>,
        document.body
      )}
    </div>
  );
}
